import { useMemo } from 'react';
import { create } from 'zustand';
import { MESSAGES, LANGS } from './messages';
import type { Lang } from './messages';

export type { Lang };
export { LANGS };

/** localStorage key holding the player's language choice. */
export const LANG_STORAGE_KEY = 'etycoon-lang';

const isLang = (value: unknown): value is Lang => value === 'cs' || value === 'en';

/** Saved choice → browser locale → English. Never throws (private mode safe). */
export const detectLang = (): Lang => {
  try {
    const saved = window.localStorage.getItem(LANG_STORAGE_KEY);
    if (isLang(saved)) return saved;
  } catch {
    /* storage unavailable — fall through to the browser locale */
  }
  try {
    const nav = (navigator.language || 'en').toLowerCase();
    if (nav.startsWith('cs') || nav.startsWith('sk')) return 'cs';
  } catch {
    /* no navigator (SSR / tests) */
  }
  return 'en';
};

export interface LangState {
  lang: Lang;
  setLang: (lang: Lang) => void;
  toggle: () => void;
}

/** Language store: single source of truth for both React and non-React code. */
export const useLangStore = create<LangState>()((set, get) => ({
  lang: detectLang(),
  setLang: (lang) => {
    set({ lang });
    try {
      window.localStorage.setItem(LANG_STORAGE_KEY, lang);
    } catch {
      /* storage unavailable — the choice stays in memory for this session */
    }
    try {
      document.documentElement.lang = lang;
    } catch {
      /* no document (tests) */
    }
  },
  toggle: () => get().setLang(get().lang === 'cs' ? 'en' : 'cs'),
}));

/** Interpolate `{placeholders}` — unknown placeholders are left untouched. */
export const interpolate = (template: string, params?: Record<string, string | number>): string => {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, key: string) => (key in params ? String(params[key]) : match));
};

/** Pure translator — safe to call from the store, bots, scripts and components. */
export const translate = (lang: Lang, key: string, params?: Record<string, string | number>): string => {
  const entry = MESSAGES[key];
  if (!entry) return key;
  return interpolate(entry[lang] ?? entry.en, params);
};

/** Imperative translator bound to the CURRENT language (used by store events / tickers). */
export const t = (key: string, params?: Record<string, string | number>): string =>
  translate(useLangStore.getState().lang, key, params);

/** React hook: re-renders the component whenever the language changes. */
export const useT = () => {
  const lang = useLangStore((state) => state.lang);
  return useMemo(() => (key: string, params?: Record<string, string | number>) => translate(lang, key, params), [lang]);
};

/** Localized tile name with a graceful fallback to whatever is on the board. */
export const tileName = (name: string, translator: (key: string) => string): string => {
  const key = `tile.${name}`;
  const value = translator(key);
  if (value !== key) return value;
  // Annexed parcels are suffixed with the round ("Industrial Park 15") — localize the stem.
  const stemMatch = name.match(/^(.*?)\s(\d+)$/);
  if (stemMatch) {
    const stem = translator(`tile.${stemMatch[1]}`);
    if (stem !== `tile.${stemMatch[1]}`) return `${stem} ${stemMatch[2]}`;
  }
  return name;
};

/** Localized tile name bound to the current language (store-side helper). */
export const tTile = (name: string): string => tileName(name, (key) => translate(useLangStore.getState().lang, key));

/** Emergency English→Czech floor for moved keys; used by the i18n verification script. */
export const missingKeys = (lang: Lang): string[] =>
  Object.entries(MESSAGES)
    .filter(([, value]) => !value[lang] || value[lang].trim().length === 0)
    .map(([key]) => key);
